import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * The standard block of facts about one record, for a narrow panel or drawer.
 *
 * Spec: `design/components/data-display/detail-card.md`. Values inside it follow
 * `design/conventions.md`.
 *
 * WHY IT IS SHARED. The customer drawer had a local `Card` and FIVE other cards that re-implemented
 * its exact markup by hand, so "standardise the cards" started by deleting four copies of the
 * chrome. Anything that renders facts about a record in a column narrower than ~480px should reach
 * for this rather than the sixth copy.
 */

export interface DetailCardProps {
  /** Header label. Sentence case — uppercase at 12px is measurably slower to read. */
  title: string;
  /** Rendered as `(7)` beside the title. Omit when zero rather than rendering `(0)`. */
  count?: number | null;
  /** Muted header suffix naming what the facts are about, e.g. `· this booking`. */
  scope?: string;
  /** The single header control. It means "open this elsewhere" — never a mutation. */
  nav?: React.ReactNode;
  /** One sentence that answers the card's question before any row does. Max one per card. */
  lead?: React.ReactNode;
  /** Warning strip above the body, for when the card is not showing what the reader assumes. */
  notice?: React.ReactNode;
  /** Footer left: level-2 disclosure. Footer right: at most one action. */
  footerAction?: React.ReactNode;
  /** Dashed + faded, for a card whose data source has not synced yet. */
  pending?: boolean;
  className?: string;
  children?: React.ReactNode;
  /** `aria-label` override; defaults to `title`. */
  label?: string;
}

export function DetailCard({
  title, count, scope, nav, lead, notice, footerAction, pending, className, children, label,
}: DetailCardProps) {
  return (
    <section
      aria-label={label ?? title}
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-bg-surface shadow-[var(--card-shadow)]',
        pending && 'border-dashed opacity-60',
        className,
      )}
    >
      <div className="flex min-h-[calc(var(--context-header-height)-4px)] items-center justify-between gap-2 px-[var(--context-padding)]">
        <span className="min-w-0 truncate text-[12px] font-medium text-text-secondary">
          {title}
          {typeof count === 'number' && count > 0 && (
            <span className="ml-1 font-normal text-text-muted">({count})</span>
          )}
          {scope && <span className="ml-1 font-normal text-text-muted">{scope}</span>}
        </span>
        {nav}
      </div>

      <div className="px-[var(--context-padding)] pb-[var(--context-padding)] pt-1">
        {notice}
        {lead && (
          <p className="mb-2 text-[16px] font-medium leading-[1.35] tracking-[-0.012em] text-text-primary">
            {lead}
          </p>
        )}
        {children}
      </div>

      {footerAction && (
        <div className="flex min-h-7 items-center justify-between gap-2 px-[var(--context-padding)] pb-[var(--context-padding)]">{footerAction}</div>
      )}
    </section>
  );
}

/** A label/value pair. Values WRAP — a truncated name is a lie by omission. */
export function DetailRow({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-2.5 py-[3px]">
      <span className="w-[84px] shrink-0 text-[12px] text-text-muted">{k}</span>
      <span
        className={cn('min-w-0 flex-1 break-words text-[13.5px] text-text-primary', mono && 'font-mono tabular-nums text-[13px]')}
        dir="auto"
      >
        {v ?? '—'}
      </span>
    </div>
  );
}

/**
 * The level-2 disclosure trigger. Bottom-left, carrying its count — position is what teaches the
 * reader this means "more of the same", as against the header icon which means "leave".
 */
export function DetailDisclosureButton({
  open, onToggle, children, controls,
}: { open: boolean; onToggle: () => void; children: React.ReactNode; controls: string }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 text-[12px] text-text-secondary hover:text-text-primary"
    >
      <ChevronRight
        className={cn('size-3 shrink-0 text-text-muted transition-transform duration-200 ease-out motion-reduce:transition-none',
          open && 'rotate-90')}
        aria-hidden
      />
      {children}
    </button>
  );
}

/**
 * The expanding panel.
 *
 * Animates a MEASURED height, not a guessed `max-height`. A fixed ceiling either clips long content
 * or makes the easing wrong for short content, and both are wrong on a panel whose row count varies
 * per record. The `0fr → 1fr` grid trick is not used either: with `overflow:hidden` on the item its
 * automatic minimum size is zero, so `1fr` can resolve to 0 and the panel silently never opens —
 * observed while building the reference page.
 *
 * Settles to `height: auto` after opening, so content that grows later (a lazy query resolving)
 * is not clipped by a height measured before it arrived.
 */
export function DetailDisclosure({ id, open, children, className }: { id: string; open: boolean; children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  /*
   * Whether the panel is closed AND finished closing.
   *
   * A panel hidden by `height:0; overflow:hidden` is still in the accessibility tree and still in
   * the tab order — a screen reader reads content nobody can see, and Tab stops on buttons that
   * are not on screen. So at rest it is also made `visibility:hidden` and `aria-hidden`, which
   * removes it from both. Not DURING the animation, because that would hide what is moving; and
   * `display:none` is never used, because there would be no height left to animate.
   */
  const [atRest, setAtRest] = useState(!open);

  useEffect(() => {
    const el = ref.current;
    const content = inner.current;
    if (!el || !content) return;

    // No animation on the first paint — a card restored already-open must not play an opening.
    if (first.current) {
      first.current = false;
      el.style.height = open ? 'auto' : '0px';
      setAtRest(!open);
      return;
    }

    let frame = 0;
    let nextFrame = 0;
    const settle = () => {
      el.style.height = open ? 'auto' : '0px';
      setAtRest(!open);
    };
    const done = (e: TransitionEvent) => {
      if (e.target === el && e.propertyName === 'height') settle();
    };
    el.addEventListener('transitionend', done);
    if (open) {
      setAtRest(false);
      // Keep the current rendered height as the start. Moving to the measured target in the
      // next frame gives the browser two distinct layouts; writing it in this effect made
      // short disclosures jump straight to their final height.
      el.style.height = `${el.getBoundingClientRect().height}px`;
      frame = requestAnimationFrame(() => {
        nextFrame = requestAnimationFrame(() => { el.style.height = `${content.offsetHeight}px`; });
      });
    } else {
      el.style.height = `${el.getBoundingClientRect().height}px`;
      frame = requestAnimationFrame(() => {
        nextFrame = requestAnimationFrame(() => { el.style.height = '0px'; });
      });
    }
    // Reduced motion and hidden tabs may never dispatch transitionend. Always settle to auto
    // after opening so asynchronously loaded rows cannot be clipped to the first measurement.
    const timer = setTimeout(settle, open ? 250 : 210);
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(nextFrame);
      clearTimeout(timer);
      el.removeEventListener('transitionend', done);
    };
  }, [open]);

  return (
    <div
      id={id}
      ref={ref}
      // Collapse is faster than expand: going away should never make anyone wait.
      className={cn('overflow-hidden transition-[height] ease-out motion-reduce:transition-none',
        open ? 'duration-200' : 'duration-150',
        atRest && !open && 'invisible',
        className)}
      aria-hidden={atRest && !open ? true : undefined}
      style={{ height: 0 }}
    >
      <div ref={inner}>{children}</div>
    </div>
  );
}

/**
 * Convenience wiring for the common case: one disclosure per card, remembered for the session.
 *
 * `storageKey` persists the open/closed state per record, because a panel that re-collapses every
 * time a new message arrives closes the list the agent just opened, mid-call.
 */
export function useDetailDisclosure(storageKey?: string) {
  const id = useId();
  const [open, setOpen] = useState(() => {
    if (!storageKey || typeof sessionStorage === 'undefined') return false;
    try { return sessionStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      if (storageKey && typeof sessionStorage !== 'undefined') {
        try { sessionStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* private mode */ }
      }
      return next;
    });
  }, [storageKey]);
  return { id, open, toggle };
}
